import React, { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isAxiosError } from "axios";
import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import {
  ArrowBackRounded,
  ContentCopyOutlined,
  DeleteOutlineRounded,
  DescriptionOutlined,
  EditNoteOutlined,
  SchemaOutlined,
  TranslateRounded,
  UploadFileOutlined,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import {
  DocumentCraftingDocumentDetail,
  DocumentCraftingRevisionOrigin,
  DocumentCraftingRunStatus,
  DocumentCraftingType,
} from "../../../Models/documentCrafting";
import {
  useDeleteDocumentCraftingDocument,
  useDiscardDocumentCraftingDraft,
  useDocumentCraftingDocument,
  useDocumentCraftingRevision,
  useDocumentCraftingTypes,
} from "../../../Query/useDocumentCrafting";
import ConfirmDialog from "../Common/ConfirmDialog";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  Panel,
} from "../Common/DocumentCraftingUi";
import PagePreview from "../Common/PagePreview";
import QualityNotes from "../Common/QualityNotes";
import RevisionTimeline from "../Common/RevisionTimeline";
import { documentCraftingPaths } from "../Common/documentCraftingNavigation";
import { formatDate, formatDateTime } from "../Common/format";
import { useDocumentCraftingEnumLabels } from "../Common/useDocumentCraftingEnumLabels";
import DocumentHeader from "./DocumentHeader";
import DocumentNotices from "./DocumentNotices";
import DocumentUsagePanel from "./DocumentUsagePanel";
import DraftPanel from "./DraftPanel";
import RevisionDownloads from "./RevisionDownloads";
import ShareDialog from "./ShareDialog";
import TransferOwnershipDialog from "./TransferOwnershipDialog";
import TranslationButton, { useTranslationPreview } from "./TranslationButton";
import UploadRevisionDialog from "./UploadRevisionDialog";
import InsertDiagramDialog from "./InsertDiagramDialog";
import { DocumentAction } from "./DocumentActionsMenu";
import {
  DRAFT_PARAM,
  fallbackNativeExt,
  formFieldLabel,
  isRunWorking,
  otherLanguage,
  previewUnit,
  selectRevision,
} from "./detailModel";

type DialogKey = "share" | "transfer" | "upload" | "delete" | "draftExists" | "insertDiagram";

const DocumentDetailView: React.FC<{
  document: DocumentCraftingDocumentDetail;
  isFetching: boolean;
}> = ({ document, isFetching }) => {
  const { t, i18n } = useTranslation("documentCrafting");
  const navigate = useNavigate();
  const labels = useDocumentCraftingEnumLabels();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dialog, setDialog] = useState<DialogKey | null>(null);
  const types = useDocumentCraftingTypes();
  const deleteDocument = useDeleteDocumentCraftingDocument();
  const discardDraft = useDiscardDocumentCraftingDraft();

  const { selection, missing } = selectRevision(
    document,
    searchParams.get("revision"),
  );
  const saved = selection.kind === "saved" ? selection.revision : null;
  const draft = document.my_draft_revision;
  // With nothing saved yet, the creation draft tells how the first run went.
  const shownRevisionId =
    selection.kind === "none" ? draft?.id : selection.revision.id;
  const revision = useDocumentCraftingRevision(document.id, shownRevisionId);
  const detail = revision.data?.id === shownRevisionId ? revision.data : undefined;

  const typeDef = types.data?.types.find((item) => item.id === document.type);
  const nativeExt = typeDef?.native_ext ?? fallbackNativeExt(document.type_key);
  const unit = previewUnit(document.type_key);
  const canEdit = document.permissions.can_edit;
  const canManage = document.permissions.can_manage;
  // Personal documents (no workspace) have no shares, visibility or transfer.
  const canShare = canManage && !document.is_personal;
  const canUpload =
    canEdit &&
    (typeDef ? typeDef.supports_manual_upload : ["docx", "pptx"].includes(nativeExt));
  const translationLanguage = otherLanguage(document.language);

  const translation = useTranslationPreview({
    documentId: document.id,
    title: document.title,
    revisionId: saved?.id ?? 0,
    revisionNumber: saved?.number ?? null,
    language: translationLanguage,
    translations: saved?.translations ?? [],
  });

  const showRevision = (value: string | null) =>
    setSearchParams(value ? { revision: value } : {});

  const startNewRevision = () => {
    if (draft) setDialog("draftExists");
    else navigate(documentCraftingPaths.revise(document.id, { base: saved?.id }));
  };

  const discardAndRevise = () => {
    if (!draft) return;
    discardDraft.mutate(
      { documentId: document.id, revisionId: draft.id },
      {
        onSuccess: () => {
          setDialog(null);
          navigate(documentCraftingPaths.revise(document.id, { base: saved?.id }));
        },
      },
    );
  };

  const actions: DocumentAction[] = [];
  if (saved && canEdit) {
    actions.push({
      key: "revise",
      label: t("detail.actions.newRevision"),
      icon: <EditNoteOutlined fontSize="small" />,
      onClick: startNewRevision,
    });
  }
  if (saved && canUpload) {
    actions.push({
      key: "upload",
      label: t("detail.actions.uploadRevision"),
      icon: <UploadFileOutlined fontSize="small" />,
      onClick: () => setDialog("upload"),
    });
  }
  if (saved && document.type === DocumentCraftingType.DIAGRAM) {
    actions.push({
      key: "insertDiagram",
      label: t("detail.actions.insertDiagram"),
      icon: <SchemaOutlined fontSize="small" />,
      onClick: () => setDialog("insertDiagram"),
    });
  }
  if (saved) {
    actions.push({
      key: "translate",
      label: t("detail.actions.previewIn", {
        language: labels.language(translationLanguage),
      }),
      icon: <TranslateRounded fontSize="small" />,
      onClick: translation.request,
      disabled: translation.working,
    });
    actions.push({
      key: "duplicate",
      label: t("detail.actions.duplicate"),
      icon: <ContentCopyOutlined fontSize="small" />,
      onClick: () =>
        navigate(
          documentCraftingPaths.create({
            duplicate: { documentId: document.id, revisionId: saved.id },
          }),
        ),
    });
  }
  if (canManage) {
    actions.push({
      key: "delete",
      label: t("detail.actions.delete"),
      icon: <DeleteOutlineRounded fontSize="small" />,
      onClick: () => setDialog("delete"),
      danger: true,
    });
  }

  const fieldLabel = (path: string) =>
    formFieldLabel(typeDef?.fields, path, i18n.language);
  const editField =
    saved && canEdit
      ? (field: string) =>
          navigate(documentCraftingPaths.revise(document.id, { base: saved.id, field }))
      : undefined;

  const selector = (document.revisions.length > 0 || draft) && (
    <TextField
      select
      size="small"
      label={t("detail.selector.label")}
      value={
        selection.kind === "draft"
          ? DRAFT_PARAM
          : saved
            ? String(saved.number)
            : ""
      }
      onChange={(event) => showRevision(event.target.value)}
      sx={{ minWidth: 240 }}
    >
      {draft && (
        <MenuItem value={DRAFT_PARAM}>{t("detail.selector.draft")}</MenuItem>
      )}
      {document.revisions.map((item, index) => (
        <MenuItem key={item.id} value={String(item.number)}>
          {t("detail.selector.revision", {
            number: item.number,
            date: formatDate(item.saved_at, i18n.language),
          })}
          {index === 0 && ` · ${t("detail.selector.latest")}`}
        </MenuItem>
      ))}
    </TextField>
  );

  let main: React.ReactNode;
  if (selection.kind === "draft") {
    main = (
      <DraftPanel
        document={document}
        draft={selection.revision}
        detail={detail}
        detailError={revision.isError}
        onRetryDetail={() => revision.refetch()}
        nativeExt={nativeExt}
        unit={unit}
      />
    );
  } else if (saved) {
    const pageCount = detail?.artefacts.pages ?? saved.page_count ?? 0;
    main = (
      <Panel
        title={t("detail.preview.title")}
        subtitle={t("detail.preview.subtitle", {
          number: saved.number,
          date: formatDateTime(saved.saved_at, i18n.language),
        })}
      >
        <Box sx={{ mb: 2 }}>
          <RevisionDownloads
            documentId={document.id}
            title={document.title}
            revisionId={saved.id}
            revisionNumber={saved.number}
            artefacts={detail?.artefacts}
            nativeExt={nativeExt}
            viewerTitle={`${document.title} · ${t("common.revisions.revision", {
              number: saved.number,
            })}`}
          >
            <TranslationButton preview={translation} />
          </RevisionDownloads>
        </Box>
        {saved.origin === DocumentCraftingRevisionOrigin.MANUAL_UPLOAD &&
          saved.ingest_confidence !== null && (
            <Box
              sx={{
                mb: 2,
                p: 1.25,
                borderRadius: "10px",
                backgroundColor: color.gray50,
                border: `1px solid ${color.gray200}`,
              }}
            >
              <Typography sx={{ fontSize: 13, color: color.gray700 }}>
                {t("detail.preview.ingest", {
                  confidence: labels.ingestConfidence(saved.ingest_confidence),
                })}
              </Typography>
              {(detail?.ingest_notes ?? []).length > 0 && (
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                  {detail?.ingest_notes.map((note, index) => (
                    <Typography
                      component="li"
                      key={index}
                      sx={{ fontSize: 12, color: color.gray600 }}
                    >
                      {note}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        {revision.isError && !detail ? (
          <ErrorState
            onRetry={() => revision.refetch()}
            text={t("detail.preview.revisionError")}
          />
        ) : (
          <PagePreview
            documentId={document.id}
            revisionId={saved.id}
            pageCount={pageCount}
            unit={unit}
          />
        )}
      </Panel>
    );
  } else {
    // Nothing saved yet: the first run is working, waiting or failed.
    const run = document.active_run ?? detail?.last_run ?? null;
    const failed =
      document.creation_failed ||
      run?.status === DocumentCraftingRunStatus.FAILED ||
      run?.status === DocumentCraftingRunStatus.CANCELLED;
    main = (
      <Panel>
        <EmptyState
          icon={<DescriptionOutlined />}
          title={
            failed
              ? t("detail.creation.failed")
              : run?.status === DocumentCraftingRunStatus.AWAITING_ANSWERS
                ? t("detail.creation.awaiting")
                : isRunWorking(run)
                  ? t("detail.creation.working", { progress: run?.progress ?? 0 })
                  : t("detail.creation.empty")
          }
          text={failed ? t("detail.creation.failedText") : undefined}
          action={
            run ? (
              <Button
                variant="contained"
                onClick={() => navigate(documentCraftingPaths.run(run.id))}
              >
                {failed
                  ? t("detail.notices.openFailedRun")
                  : run.status === DocumentCraftingRunStatus.AWAITING_ANSWERS
                    ? t("detail.notices.answerQuestions")
                    : t("detail.notices.openRun")}
              </Button>
            ) : undefined
          }
        />
      </Panel>
    );
  }

  return (
    <>
      <DocumentHeader
        document={document}
        onNewRevision={saved && canEdit ? startNewRevision : undefined}
        onShare={canShare ? () => setDialog("share") : undefined}
        actions={actions}
      />
      <DocumentNotices
        document={document}
        selection={selection}
        missingRevision={isFetching ? null : missing}
        onOpenDraft={() => showRevision(DRAFT_PARAM)}
        onTransfer={canShare ? () => setDialog("transfer") : undefined}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1fr) 400px" },
          gap: 3,
          alignItems: "start",
        }}
      >
        <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
          {selector && <Box>{selector}</Box>}
          {main}
        </Box>

        <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
          {selection.kind !== "none" && (
            <Panel
              title={t("detail.quality.title")}
              subtitle={t("detail.quality.subtitle")}
            >
              {detail ? (
                <QualityNotes
                  findings={detail.findings}
                  unit={unit}
                  fieldLabel={fieldLabel}
                  onEditField={editField}
                />
              ) : revision.isError ? (
                <ErrorState onRetry={() => revision.refetch()} />
              ) : (
                <LoadingState />
              )}
            </Panel>
          )}
          <Panel title={t("detail.history.title")}>
            <RevisionTimeline
              revisions={document.revisions}
              draft={draft}
              selectedRevisionId={selection.kind === "none" ? null : selection.revision.id}
              onSelect={(item) =>
                showRevision(
                  item.id === draft?.id ? DRAFT_PARAM : String(item.number),
                )
              }
            />
          </Panel>
          {document.usage_visible && <DocumentUsagePanel documentId={document.id} />}
        </Box>
      </Box>

      {canShare && (
        <>
          <ShareDialog
            open={dialog === "share"}
            document={document}
            onClose={() => setDialog(null)}
            onTransfer={() => setDialog("transfer")}
          />
          <TransferOwnershipDialog
            open={dialog === "transfer"}
            document={document}
            onClose={() => setDialog(null)}
          />
        </>
      )}
      {canManage && (
        <>
          <ConfirmDialog
            open={dialog === "delete"}
            title={t("detail.delete.title")}
            text={t("detail.delete.text", { title: document.title })}
            confirmLabel={t("detail.delete.confirm")}
            pending={deleteDocument.isPending}
            danger
            onConfirm={() =>
              deleteDocument.mutate(document.id, {
                onSuccess: () => navigate(documentCraftingPaths.list),
              })
            }
            onClose={() => setDialog(null)}
          />
        </>
      )}
      {saved && document.type === DocumentCraftingType.DIAGRAM && dialog === "insertDiagram" && (
        <InsertDiagramDialog
          open
          documentId={document.id}
          revisionId={saved.id}
          onClose={() => setDialog(null)}
          onStarted={(runId) => navigate(documentCraftingPaths.run(runId))}
        />
      )}
      {canUpload && document.revisions.length > 0 && (
        <UploadRevisionDialog
          open={dialog === "upload"}
          documentId={document.id}
          nativeExt={nativeExt}
          maxUploadMb={document.max_upload_mb}
          revisions={document.revisions}
          defaultBaseId={saved?.id}
          hasDraft={!!draft}
          onOpenDraft={() => {
            setDialog(null);
            showRevision(DRAFT_PARAM);
          }}
          onClose={() => setDialog(null)}
          onStarted={(runId) => navigate(documentCraftingPaths.run(runId))}
        />
      )}
      {draft && (
        <ConfirmDialog
          open={dialog === "draftExists"}
          title={t("detail.draftExists.title")}
          text={t("detail.draftExists.text", {
            date: formatDateTime(draft.created_at, i18n.language),
          })}
          confirmLabel={t("detail.draftExists.continue")}
          pending={discardDraft.isPending}
          secondaryAction={{
            label: t("detail.draftExists.discard"),
            onClick: discardAndRevise,
          }}
          onConfirm={() => {
            setDialog(null);
            showRevision(DRAFT_PARAM);
          }}
          onClose={() => setDialog(null)}
        />
      )}
    </>
  );
};

/** Detail page of one document (`/document-crafting/:documentId?revision=N`). */
const DocumentDetail: React.FC = () => {
  const { t } = useTranslation("documentCrafting");
  const navigate = useNavigate();
  const params = useParams();
  const documentId = Number(params.documentId);
  const valid = Number.isInteger(documentId) && documentId > 0;
  const document = useDocumentCraftingDocument(valid ? documentId : undefined);

  const status = isAxiosError(document.error) ? document.error.response?.status : undefined;
  if (!valid || status === 404 || status === 403) {
    return (
      <Panel>
        <EmptyState
          icon={<DescriptionOutlined />}
          title={t("detail.notFound")}
          action={
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ArrowBackRounded />}
              onClick={() => navigate(documentCraftingPaths.list)}
            >
              {t("nav.backToList")}
            </Button>
          }
        />
      </Panel>
    );
  }
  if (document.data) {
    return (
      <DocumentDetailView document={document.data} isFetching={document.isFetching} />
    );
  }
  if (document.isError) {
    return (
      <Panel>
        <ErrorState onRetry={() => document.refetch()} />
      </Panel>
    );
  }
  return <LoadingState />;
};

export default DocumentDetail;
